/**
 * Servicio de Suscripciones - LocalCenter
 * Maneja todas las operaciones de suscripcion con el backend
 */

import { Injectable, signal, computed } from '@angular/core';
import { Observable, tap, catchError, of, map, shareReplay } from 'rxjs';
import { environment } from '../environment/environment';
import { BaseHttpService } from '../core/services/base-http.service';
import { Logger } from '../shared/utils/logger.utils';
import {
  Plan,
  Suscripcion,
  UsoRecurso,
  UsoSuscripcion,
  SuscripcionActualResponse,
  Factura,
  HistorialItem,
  VerificarLimiteResponse,
  ValidarCodigoResponse,
  CambiarPlanResponse,
} from '../models/suscripcion.model';

export type {
  Plan,
  Suscripcion,
  UsoRecurso,
  UsoSuscripcion,
  SuscripcionActualResponse,
  Factura,
  HistorialItem,
  VerificarLimiteResponse,
  ValidarCodigoResponse,
  CambiarPlanResponse,
} from '../models/suscripcion.model';

@Injectable({
  providedIn: 'root',
})
export class SuscripcionService extends BaseHttpService {
  protected override baseUrl = `${environment.apiUrl}/suscripciones`;

  // Estado reactivo
  private _suscripcionActual = signal<Suscripcion | null>(null);
  private _uso = signal<UsoSuscripcion | null>(null);
  private _alertas = signal<string[]>([]);
  private _planes = signal<Plan[]>([]);
  private _cargando = signal<boolean>(false);
  private planesCache$: Observable<Plan[]> | null = null;

  // Computed signals publicos
  readonly suscripcionActual = this._suscripcionActual.asReadonly();
  readonly uso = this._uso.asReadonly();
  readonly alertas = this._alertas.asReadonly();
  readonly planes = this._planes.asReadonly();
  readonly cargando = this._cargando.asReadonly();

  readonly planActual = computed(() => {
    const suscripcion = this._suscripcionActual();
    if (!suscripcion) return null;
    return this._planes().find((p) => p.id === suscripcion.planId) || null;
  });

  readonly esTrial = computed(
    () => this._suscripcionActual()?.esTrial ?? false,
  );

  readonly diasRestantesTrial = computed(() => {
    const suscripcion = this._suscripcionActual();
    if (!suscripcion?.esTrial || !suscripcion.fechaFinTrial) return null;
    const ahora = new Date();
    const fin = new Date(suscripcion.fechaFinTrial);
    return Math.max(
      0,
      Math.ceil((fin.getTime() - ahora.getTime()) / (1000 * 60 * 60 * 24)),
    );
  });

  readonly estadoSuscripcion = computed(
    () => this._suscripcionActual()?.estado ?? 'sin_suscripcion',
  );

  readonly tieneAlertasUrgentes = computed(() => {
    const alertas = this._alertas();
    return alertas.some(
      (a) =>
        a.includes('vencido') ||
        a.includes('terminado') ||
        a.includes('cancelara') ||
        a.includes('100%'),
    );
  });

  // =============================================
  // PLANES
  // =============================================

  /**
   * Obtener todos los planes disponibles
   */
  getPlanes(incluirEnterprise: boolean = false): Observable<Plan[]> {
    if (!this.planesCache$) {
      this.planesCache$ = this.get<Plan[]>('/planes', {
        params: { incluirEnterprise },
      }).pipe(
        tap((response) => {
          if (response.success) {
            this._planes.set(response.data);
          }
        }),
        map((response) => response.data),
        catchError((error) => {
          this.planesCache$ = null;
          return of([]);
        }),
        shareReplay(1),
      );
    }
    return this.planesCache$;
  }

  /**
   * Obtener detalle de un plan
   */
  getPlan(planId: number): Observable<Plan | null> {
    return this.get<Plan>(`/planes/${planId}`).pipe(
      map((response) => response.data),
      catchError(() => {
        return of(null);
      }),
    );
  }

  // =============================================
  // SUSCRIPCION ACTUAL
  // =============================================

  /**
   * Obtener suscripcion actual con uso y alertas
   */
  getSuscripcionActual(): Observable<SuscripcionActualResponse> {
    this._cargando.set(true);

    return this.get<SuscripcionActualResponse>('/actual', {
      silentError: true,
    }).pipe(
      tap((response) => {
        if (response.success) {
          this._suscripcionActual.set(response.data.suscripcion);
          this._uso.set(response.data.uso);
          this._alertas.set(response.data.alertas);
        }
        this._cargando.set(false);
      }),
      map((response) => response.data),
      catchError((error) => {
        this._cargando.set(false);
        return of({
          suscripcion: null,
          uso: null,
          facturasPendientes: 0,
          alertas: ['Error al cargar suscripcion'],
        });
      }),
    );
  }

  /**
   * Obtener solo el uso actual
   */
  getUsoActual(): Observable<{
    uso: UsoSuscripcion | null;
    alertas: string[];
  }> {
    return this.get<{ uso: UsoSuscripcion | null; alertas: string[] }>('/uso', {
      silentError: true,
    }).pipe(
      tap((response) => {
        if (response.success && response.data.uso) {
          this._uso.set(response.data.uso);
        }
      }),
      map((response) => response.data),
      catchError(() => {
        return of({ uso: null, alertas: [] });
      }),
    );
  }

  /**
   * Refrescar datos de suscripcion (para llamar despues de cambios)
   */
  refrescar(): void {
    this.getSuscripcionActual().subscribe({
      error: (err) => Logger.error('Error al refrescar suscripcion:', err),
    });
  }

  // =============================================
  // VERIFICACION DE LIMITES
  // =============================================

  /**
   * Verificar si puede crear un recurso
   */
  verificarLimite(
    tipo: 'usuarios' | 'productos' | 'almacenes' | 'pedidos',
  ): Observable<VerificarLimiteResponse> {
    return this.get<VerificarLimiteResponse>(`/verificar-limite/${tipo}`, {
      silentError: true,
    }).pipe(
      map((response) => response.data),
      catchError(() => {
        return of({
          puedeCrear: false,
          actual: 0,
          limite: 0,
          porcentajeUso: 0,
          mensajeError: 'Error al verificar limite',
          requiereUpgrade: true,
        });
      }),
    );
  }

  /**
   * Verificar si tiene acceso a una feature
   */
  verificarFeature(feature: string): Observable<boolean> {
    return this.get<{ feature: string; tieneAcceso: boolean }>(
      `/verificar-feature/${feature}`,
      { silentError: true },
    ).pipe(
      map((response) => response.data.tieneAcceso),
      catchError(() => {
        return of(false);
      }),
    );
  }

  // =============================================
  // GESTION DE SUSCRIPCION
  // =============================================

  /**
   * Cambiar de plan
   */
  cambiarPlan(
    planId: number,
    cicloFacturacion: 'mensual' | 'anual',
    codigoPromocional?: string,
  ): Observable<CambiarPlanResponse> {
    this._cargando.set(true);

    return this.post<CambiarPlanResponse>('/cambiar-plan', {
      planId,
      cicloFacturacion,
      codigoPromocional,
    }).pipe(
      tap((response) => {
        if (response.success) {
          this._suscripcionActual.set(response.data.suscripcion);
          this.refrescar();
        }
        this._cargando.set(false);
      }),
      map((response) => response.data),
      catchError((error) => {
        this._cargando.set(false);
        throw error;
      }),
    );
  }

  /**
   * Cancelar suscripcion
   */
  cancelar(
    motivo?: string,
    cancelarInmediatamente: boolean = false,
  ): Observable<Suscripcion> {
    this._cargando.set(true);

    return this.post<Suscripcion>('/cancelar', {
      motivo,
      cancelarInmediatamente,
    }).pipe(
      tap((response) => {
        if (response.success) {
          this._suscripcionActual.set(response.data);
        }
        this._cargando.set(false);
      }),
      map((response) => response.data),
      catchError((error) => {
        this._cargando.set(false);
        throw error;
      }),
    );
  }

  /**
   * Pausar suscripcion
   */
  pausar(): Observable<Suscripcion> {
    this._cargando.set(true);

    return this.post<Suscripcion>('/pausar', {}).pipe(
      tap((response) => {
        if (response.success) {
          this._suscripcionActual.set(response.data);
        }
        this._cargando.set(false);
      }),
      map((response) => response.data),
      catchError((error) => {
        this._cargando.set(false);
        throw error;
      }),
    );
  }

  /**
   * Reactivar suscripcion
   */
  reactivar(): Observable<Suscripcion> {
    this._cargando.set(true);

    return this.post<Suscripcion>('/reactivar', {}).pipe(
      tap((response) => {
        if (response.success) {
          this._suscripcionActual.set(response.data);
        }
        this._cargando.set(false);
      }),
      map((response) => response.data),
      catchError((error) => {
        this._cargando.set(false);
        throw error;
      }),
    );
  }

  // =============================================
  // FACTURACION
  // =============================================

  /**
   * Obtener facturas
   */
  getFacturas(limit: number = 10): Observable<Factura[]> {
    return this.get<Factura[]>('/facturas', {
      params: { limit },
      silentError: true,
    }).pipe(
      map((response) => response.data),
      catchError(() => {
        return of([]);
      }),
    );
  }

  /**
   * Obtener historial de cambios
   */
  getHistorial(): Observable<HistorialItem[]> {
    return this.get<HistorialItem[]>('/historial', { silentError: true }).pipe(
      map((response) => response.data),
      catchError(() => {
        return of([]);
      }),
    );
  }

  // =============================================
  // CODIGOS PROMOCIONALES
  // =============================================

  /**
   * Validar codigo promocional
   */
  validarCodigo(
    codigo: string,
    planId: number,
    cicloAnual: boolean = false,
  ): Observable<ValidarCodigoResponse> {
    return this.post<ValidarCodigoResponse>(
      '/validar-codigo',
      {
        codigo,
        planId,
        cicloAnual,
      },
      { silentError: true },
    ).pipe(
      map((response) => response.data),
      catchError(() => {
        return of({ valido: false, mensaje: 'Error al validar codigo' });
      }),
    );
  }

  // =============================================
  // HELPERS
  // =============================================

  /**
   * Obtener color de estado para UI
   */
  getColorEstado(estado: string): string {
    const colores: Record<string, string> = {
      activa: '#10b981',
      trial: '#888',
      pausada: '#f59e0b',
      cancelada: '#6b7280',
      vencida: '#ef4444',
      impaga: '#ef4444',
    };
    return colores[estado] || '#6b7280';
  }

  /**
   * Obtener icono de estado para UI
   */
  getIconoEstado(estado: string): string {
    const iconos: Record<string, string> = {
      activa: 'check_circle',
      trial: 'hourglass_empty',
      pausada: 'pause_circle',
      cancelada: 'cancel',
      vencida: 'error',
      impaga: 'warning',
    };
    return iconos[estado] || 'help';
  }

  /**
   * Formatear precio para mostrar
   */
  formatearPrecio(precio: number, moneda: string = 'ARS'): string {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: moneda,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(precio);
  }

  /**
   * Obtener texto de ciclo de facturacion
   */
  getTextoCiclo(ciclo: 'mensual' | 'anual'): string {
    return ciclo === 'anual' ? 'por ano' : 'por mes';
  }

  /**
   * Calcular ahorro anual
   */
  calcularAhorroAnual(plan: Plan): number {
    if (!plan.precioAnual) return 0;
    const precioAnualSinDescuento = plan.precioMensual * 12;
    return precioAnualSinDescuento - plan.precioAnual;
  }

  /**
   * Calcular porcentaje de descuento anual
   */
  calcularDescuentoAnual(plan: Plan): number {
    if (!plan.precioAnual) return 0;
    const precioAnualSinDescuento = plan.precioMensual * 12;
    return Math.round((1 - plan.precioAnual / precioAnualSinDescuento) * 100);
  }
}
